import { useEffect, useLayoutEffect, useState } from "react";
import { useApolloClient, useLazyQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";

import { useDarkModeCheck } from "../DashboardLayout";
import {
  DOWNLOAD_DOCUMENT_FILE,
  GET_REPORT_DATA,
  GET_REPORT_DATA_TIPPING,
} from "../../../service/graphql/query";
import { logout } from "../../../service/graphql/utils";
import {
  base64ToBlob,
  retrieveFileType,
} from "../../../service/utils/functions";
import ReportingPageUI from "../../../ui/dashboard/reporting/ReportingPageUI";
import { useCustomerSelection } from "../../../service/hooks/customerData";
import { trackDownload } from "../../../service/matomo";

const FileSaver = require("file-saver");

const ReportingPage = () => {
  const navigate = useNavigate();
  const { darkMode, userCustomers } = useDarkModeCheck();

  const [width, setWidth] = useState(0);
  const [isUserTipping, setIsUserTipping] = useState(false);
  const [readyToFetch, setReadyToFetch] = useState(false);
  const [yearlyReport, setYearlyReport] = useState<string[][]>([]);
  const [monthlyReport, setMonthlyReport] = useState<string[][]>([]);
  const [currentTab, setCurrentTab] = useState(0);

  const { customerList, selectedCustomer, setSelectedCustomer } =
    useCustomerSelection(readyToFetch ? userCustomers : undefined);
  const client = useApolloClient();
  const [downloadDocFile] = useLazyQuery(DOWNLOAD_DOCUMENT_FILE);

  useLayoutEffect(() => {
    const width = window.innerWidth;
    setWidth(width);

    const handleWindowResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleWindowResize);
    return () => window.removeEventListener("resize", handleWindowResize);
  }, []);

  useEffect(() => {
    if (!userCustomers) return;
    const user = userCustomers[0];
    if (user) {
      if (user.category !== "Tipping" && !user.pageAuth.reportingPage) {
        window.history.replaceState(null, "", "/");
        navigate("/dashboard/home");
      } else {
        setIsUserTipping(user.category === "Tipping");
        setReadyToFetch(true);
      }
    } else logout(navigate);
  }, [userCustomers, navigate]);

  // Reports are fetched for the selected customer only, on page open and on
  // every customer change.
  useEffect(() => {
    if (!readyToFetch || !selectedCustomer?.value) return;
    let isCurrent = true;
    const customerId = selectedCustomer.value;

    const toTable = (reports: any[]) =>
      (reports ?? []).map((rep: any) => [
        rep.periodLabel,
        rep.excelDownloadFilepath,
        rep.PDFDownloadFilepath,
        rep.PDFViewFilepath,
      ]);

    const loadReports = async () => {
      if (isUserTipping) {
        const res = await client.query({
          query: GET_REPORT_DATA_TIPPING,
          variables: { customerId },
          context: { apiName: "middleware" },
          fetchPolicy: "no-cache",
        });
        if (!isCurrent) return;
        setYearlyReport(toTable(res.data?.getCustomers?.[0]?.reportsTipping));
        setMonthlyReport([]);
      } else {
        const [yearRes, monthRes] = await Promise.all(
          [true, false].map((isYtd) =>
            client.query({
              query: GET_REPORT_DATA,
              variables: { customerId, isYtd },
              context: { apiName: "middleware" },
              fetchPolicy: "no-cache",
            })
          )
        );
        if (!isCurrent) return;
        setYearlyReport(toTable(yearRes.data?.getCustomers?.[0]?.reports));
        setMonthlyReport(toTable(monthRes.data?.getCustomers?.[0]?.reports));
      }
    };

    setYearlyReport([]);
    setMonthlyReport([]);
    loadReports().catch((err) => console.error(err));

    return () => {
      isCurrent = false;
    };
  }, [readyToFetch, isUserTipping, selectedCustomer, client]);

  const manageFile = async (link: string, download: boolean) => {
    let downloadLink = link;
    const fileName = link.split("\\").pop();
    const fileExtension = fileName?.split(".")[1];
    if (!fileName || !fileExtension) return;
    const docFileRes = await downloadDocFile({
      variables: {
        fileId: downloadLink,
        source: "local",
      },
      context: { apiName: "middleware" },
    });
    if (!docFileRes.data?.downloadProtectedDocument) return;
    let fileType = retrieveFileType(fileExtension);
    const blob = base64ToBlob(
      docFileRes.data.downloadProtectedDocument,
      fileType
    );
    trackDownload(fileName, download ? "report" : "report-opened");
    if (download) FileSaver.saveAs(blob, fileName);
    else {
      const file = new File([blob], `${fileName}.${fileExtension}`, {
        type: fileType,
      });
      const url = URL.createObjectURL(file);
      const win = window.open(url, "_blank");
      if (win) win.focus();
    }
  };

  return (
    <ReportingPageUI
      customerList={customerList}
      currentTab={currentTab}
      isUserTipping={isUserTipping}
      yearlyReport={yearlyReport}
      monthlyReport={monthlyReport}
      setSelectedCustomer={setSelectedCustomer}
      setCurrentTab={setCurrentTab}
      manageFile={manageFile}
      darkMode={darkMode}
      width={width}
    />
  );
};

export default ReportingPage;
